import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const AllQpayCenters = () => {
  const navigate = useNavigate();

  const [qpayCenters, setQpayCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();
  const [userId, setUserId] = useState('');

  // Dialog / Modal States
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  // Block / Unblock Modal State
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [targetBlockCenter, setTargetBlockCenter] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);

  // Edit Modal State
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editCenter, setEditCenter] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', mobile: '', email: '', address: '' });
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Dropdown Lists
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);

  // Filter States
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all'); // Constituency
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
  }, []);

  const [rawTaluksList, setRawTaluksList] = useState([]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const distRes = await axios.get(`${API_BASE_URL}/districts`);
        if (distRes.status === 200 && distRes.data) {
          const list = Array.isArray(distRes.data)
            ? distRes.data
            : distRes.data.districts || [];
          setAllDistrictsList(list);
        }
      } catch (err) {
        console.error('Error fetching districts dropdown:', err);
      }

      try {
        const talukRes = await axios.get(`${API_BASE_URL}/taluks`);
        if (talukRes.status === 200 && talukRes.data) {
          const list = Array.isArray(talukRes.data)
            ? talukRes.data
            : talukRes.data.taluks || [];
          setRawTaluksList(list);
          const names = [...new Set(list.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
          setAllConstituenciesList(names.sort());
        }
      } catch (err) {
        console.error('Error fetching constituencies dropdown:', err);
      }
    };

    fetchDropdownData();
  }, []);

  // Update Constituency dropdown whenever District filter changes
  useEffect(() => {
    setVillageFilter('all');
    if (districtFilter === 'all') {
      const names = [...new Set(rawTaluksList.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    } else {
      const selectedDistObj = allDistrictsList.find(
        (d) => (d.districtName || d.name || '').toLowerCase() === districtFilter.toLowerCase()
      );
      const selectedDistId = selectedDistObj ? String(selectedDistObj._id || selectedDistObj.id) : null;

      const filteredTaluks = rawTaluksList.filter((t) => {
        const tDistId = String(t.district || t.districtId || '');
        const tDistName = (t.districtName || '').toLowerCase();
        return (
          (selectedDistId && tDistId === selectedDistId) ||
          tDistName === districtFilter.toLowerCase()
        );
      });

      const names = [...new Set(filteredTaluks.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    }
  }, [districtFilter, allDistrictsList, rawTaluksList]);

  // Fetch Q-Pay Centers Data
  const fetchQpayCenters = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        userId,
        district: districtFilter,
        village: villageFilter,
        pincode: pincodeFilter,
        search: searchQuery,
      };

      const response = await axios.post(
        `${API_BASE_URL}/qpay-centers/all`,
        payload
      );

      if (response.data && response.data.status === 'SUCCESS' && Array.isArray(response.data.data)) {
        setQpayCenters(response.data.data);
        const dataCons = [...new Set(response.data.data.flatMap((item) => item.villages || []).filter((v) => v !== 'N/A'))];
        if (dataCons.length > 0) {
          setAllConstituenciesList((prev) => [...new Set([...prev, ...dataCons])].sort());
        }
      } else {
        setQpayCenters([]);
      }
    } catch (error) {
      console.error('Error fetching Q-Pay Centers:', error);
      setQpayCenters([]);
      setDialogMessage('Failed to fetch Q-Pay Centers. Please try again.');
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  }, [userId, districtFilter, villageFilter, pincodeFilter, searchQuery]);

  useEffect(() => {
    fetchQpayCenters();
  }, [fetchQpayCenters]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenViewModal = (row) => {
    setSelectedCenter(row);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedCenter(null);
  };

  const handleOpenBlockModal = (row) => {
    if (!can('qpayCentre.all.block')) return;
    setTargetBlockCenter(row);
    setBlockReason(row.blockReason || '');
    setOpenBlockModal(true);
  };

  const handleCloseBlockModal = () => {
    setOpenBlockModal(false);
    setTargetBlockCenter(null);
    setBlockReason('');
  };

  // Toggle Block / Unblock Action (SuperAdmin Only)
  const handleToggleBlockConfirm = async () => {
    if (!targetBlockCenter || !can('qpayCentre.all.block')) return;
    const isCurrentlyBlocked = targetBlockCenter.isBlocked;

    if (!isCurrentlyBlocked && !blockReason.trim()) {
      alert('Please enter a reason for blocking this Q-Pay Center');
      return;
    }

    setBlockLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/qpay-centers/toggle-block`, {
        qpayCenterId: targetBlockCenter.id,
        isBlocked: !isCurrentlyBlocked,
        blockReason: isCurrentlyBlocked ? '' : blockReason.trim(),
      });

      if (response.status === 200 && response.data.success) {
        setDialogMessage(
          `Q-Pay Center ${!isCurrentlyBlocked ? 'blocked' : 'unblocked'} successfully.`
        );
        setOpenDialog(true);
        handleCloseBlockModal();
        fetchQpayCenters();
      } else {
        alert(response.data.message || 'Failed to update block status.');
      }
    } catch (err) {
      console.error('Error toggling block status:', err);
      alert('Server error while updating block status.');
    } finally {
      setBlockLoading(false);
    }
  };

  // CSV Export Function (SuperAdmin Only)
  const handleExportCSV = () => {
    if (!can('qpayCentre.all.export')) return;
    if (qpayCenters.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = [
      'SL No',
      'Q-Pay Centre ID',
      'State Head ID',
      'First Name',
      'List Name',
      'STATE',
      'List of DISTRICT',
      'List of CONSTITUENCY',
      'List of PINCODEs',
      'Status',
    ];

    const rows = qpayCenters.map((item) => [
      item.slNo,
      `"${item.customId || ''}"`,
      `"${item.stateHeadId || ''}"`,
      `"${item.firstName || ''}"`,
      `"${item.lastName || ''}"`,
      `"${item.state || 'Kerala'}"`,
      `"${(item.districts || []).join(', ')}"`,
      `"${(item.villages || []).join(', ')}"`,
      `"${(item.pincodes || []).join(', ')}"`,
      item.isBlocked ? 'Blocked' : 'Active',
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `All_QPay_Centres_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenEditModal = (row) => {
    if (!can('qpayCentre.all.edit')) return;
    setEditCenter(row);
    setEditForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      mobile: row.mobile && row.mobile !== 'N/A' ? String(row.mobile) : '',
      email: row.email && row.email !== 'N/A' ? row.email : '',
      address: row.address || '',
    });
    setEditErrors({});
    setEditError('');
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    if (editLoading) return;
    setOpenEditModal(false);
    setEditCenter(null);
  };

  const handleSaveEdit = async () => {
    if (!editCenter || !can('qpayCentre.all.edit')) return;
    const errs = {};
    const firstName = editForm.firstName.trim();
    const mobile = editForm.mobile.trim();
    const email = editForm.email.trim();
    if (!firstName) errs.firstName = 'First name is required';
    if (!/^\d{10}$/.test(mobile)) errs.mobile = 'Mobile number must be exactly 10 digits';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errs.email = 'Enter a valid email address';
    setEditErrors(errs);
    if (Object.keys(errs).length) return;

    setEditLoading(true);
    setEditError('');
    try {
      // Only profile fields: level / pincode are never sent from here
      await axios.put(`${API_BASE_URL}/users/update/${editCenter.id}`, {
        firstName,
        lastName: editForm.lastName.trim(),
        phoneNumber: mobile,
        email,
        address: editForm.address.trim(),
      });
      setQpayCenters((prev) =>
        prev.map((c) =>
          c.id === editCenter.id
            ? { ...c, firstName, lastName: editForm.lastName.trim(), mobile, email, address: editForm.address.trim() }
            : c
        )
      );
      setOpenEditModal(false);
      setEditCenter(null);
      setDialogMessage('Q-Pay Centre details updated successfully.');
      setOpenDialog(true);
      fetchQpayCenters();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update Q-Pay Centre. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'SL No',
      width: 70,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'customId',
      headerName: 'Q-Pay Centre ID',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateHeadId',
      headerName: 'State Head ID',
      minWidth: 140,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateStaffId',
      headerName: 'State Staff ID',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'qpayAgentId',
      headerName: 'Q-Pay Agent ID',
      minWidth: 170,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: 'List Name',
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'state',
      headerName: 'STATE',
      minWidth: 110,
      flex: 0.8,
    },
    {
      field: 'districts',
      headerName: 'List of DISTRICT',
      minWidth: 170,
      flex: 1.2,
      renderCell: (params) => (params.value || []).join(', ') || 'N/A',
    },
    {
      field: 'villages',
      headerName: 'List of CONSTITUENCY',
      minWidth: 170,
      flex: 1.2,
      renderCell: (params) => (params.value || []).join(', ') || 'N/A',
    },
    {
      field: 'pincodes',
      headerName: 'List of PINCODE’s',
      minWidth: 170,
      flex: 1.2,
      renderCell: (params) => (params.value || []).join(', ') || 'N/A',
    },
    {
      field: 'action',
      headerName: 'Action Button',
      minWidth: 150,
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box display="flex" gap={0.5} justifyContent="center" alignItems="center">
          {/* View Button */}
          <Tooltip title="View Q-Pay Centre Details">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleOpenViewModal(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Edit Button */}
          {can('qpayCentre.all.edit') && (
            <Tooltip title="Edit Q-Pay Centre">
              <IconButton
                color="info"
                size="small"
                onClick={() => handleOpenEditModal(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Block / Unblock Button */}
          {can('qpayCentre.all.block') && (
            <Tooltip
              title={params.row.isBlocked ? 'Unblock Q-Pay Centre' : 'Block Q-Pay Centre'}
            >
              <IconButton
                color={params.row.isBlocked ? 'success' : 'error'}
                size="small"
                onClick={() => handleOpenBlockModal(params.row)}
              >
                {params.row.isBlocked ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <BlockIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <BackButton />
      <br />
      <Paper elevation={3} sx={{ p: 3, pr: 4, borderRadius: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            All Q-PAY Centre
          </Typography>

          <Box display="flex" gap={1.5} flexWrap="wrap">
            {can('qpayCentre.all.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ height: 36, fontWeight: 700, borderRadius: 2 }}
              >
                Export CSV
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Filter Section */}
        <Grid container spacing={2} alignItems="center">
          {/* District wise */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="district-label">District wise</InputLabel>
              <Select
                labelId="district-label"
                value={districtFilter}
                label="District wise"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {allDistrictsList.map((dist) => {
                  const name = typeof dist === 'string' ? dist : dist.districtName || dist.name;
                  return (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency wise */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="constituency-label">Constituency wise</InputLabel>
              <Select
                labelId="constituency-label"
                value={villageFilter}
                label="Constituency wise"
                onChange={(e) => setVillageFilter(e.target.value)}
              >
                <MenuItem value="all">All Constituencies</MenuItem>
                {allConstituenciesList.map((cons) => (
                  <MenuItem key={cons} value={cons}>
                    {cons}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Pincode wise */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Pincode wise"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
              placeholder="e.g. 695006"
            />
          </Grid>

          {/* Search Input */}
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search by Q-Pay Centre ID / Mobile / Email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper elevation={3} sx={{ height: 600, width: '100%', borderRadius: 2 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading Q-PAY Centres...
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={qpayCenters}
            columns={columns}
            getRowId={(row) => row.id || row.slNo}
            rowHeight={60}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#1e293b',
                fontSize: 14,
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f1f5f9',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              },
            }}
          />
        )}
      </Paper>

      {/* View Detail Dialog Modal */}
      <Dialog
        open={openViewModal}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            backgroundColor: '#1976d2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Q-PAY Centre Details
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedCenter && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Q-Pay Centre ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {selectedCenter.customId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  State Head ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedCenter.stateHeadId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  First Name
                </Typography>
                <Typography variant="body1">{selectedCenter.firstName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Name
                </Typography>
                <Typography variant="body1">{selectedCenter.lastName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1">{selectedCenter.mobile}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Email ID
                </Typography>
                <Typography variant="body1">{selectedCenter.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  STATE
                </Typography>
                <Typography variant="body1">{selectedCenter.state}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: selectedCenter.isBlocked ? 'error.main' : 'success.main',
                  }}
                >
                  {selectedCenter.isBlocked ? 'Blocked' : 'Active'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of DISTRICT
                </Typography>
                <Typography variant="body1">
                  {(selectedCenter.districts || []).join(', ')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of Constituency
                </Typography>
                <Typography variant="body1">
                  {(selectedCenter.villages || []).join(', ')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of PINCODE’s
                </Typography>
                <Typography variant="body1">
                  {(selectedCenter.pincodes || []).join(', ')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewModal} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Q-Pay Centre Dialog */}
      <Dialog open={openEditModal} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Q-Pay Centre</DialogTitle>
        <DialogContent dividers>
          {editCenter && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Q-Pay Centre ID" value={editCenter.customId || ''} fullWidth size="small" disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="State" value={editCenter.state || ''} fullWidth size="small" disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Pincode(s)"
                  value={(editCenter.pincodes || []).join(', ')}
                  fullWidth
                  size="small"
                  disabled
                  helperText="Pincodes and level cannot be changed here"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                  error={!!editErrors.firstName}
                  helperText={editErrors.firstName}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mobile Number"
                  value={editForm.mobile}
                  onChange={(e) => setEditForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  error={!!editErrors.mobile}
                  helperText={editErrors.mobile}
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email ID"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  error={!!editErrors.email}
                  helperText={editErrors.email}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={editForm.address}
                  onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
              {editError && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error">{editError}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditModal} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block / Unblock Modal Dialog */}
      <Dialog open={openBlockModal} onClose={handleCloseBlockModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {targetBlockCenter?.isBlocked
            ? 'Unblock Q-Pay Centre'
            : 'Block Q-Pay Centre'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to{' '}
            {targetBlockCenter?.isBlocked ? 'unblock' : 'block'}{' '}
            <strong>{targetBlockCenter?.customId}</strong> (
            {targetBlockCenter?.firstName} {targetBlockCenter?.lastName})?
          </Typography>

          {!targetBlockCenter?.isBlocked && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Block Reason (Required)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseBlockModal} disabled={blockLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleToggleBlockConfirm}
            color={targetBlockCenter?.isBlocked ? 'success' : 'error'}
            variant="contained"
            disabled={blockLoading}
          >
            {blockLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : targetBlockCenter?.isBlocked ? (
              'Confirm Unblock'
            ) : (
              'Confirm Block'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Notification</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllQpayCenters;
